import classnames from 'classnames';
import LocalizedLink from 'components/LocalizedLink';
import React from 'react';
import { Helmet } from 'react-helmet';
import { isCordova } from 'utils/platform';

import { Breadcrumb } from '../../types/Breadcrumb';

const CSS = require('./Breadcrumbs.module.scss');

type BreadcrumbsProps = {
  structuredDataOnly?: boolean;
  paths: Breadcrumb[];
  className?: string;
};

const BreadcrumbsHelmet = ({ paths }: { paths: Breadcrumb[] }) => {
  const itemListElement = paths
    .filter((f) => f && f.path)
    .map((path, index) => ({
      '@type': 'ListItem',
      position: index + 1,
      name: path.text,
      item: `${window.location.origin}${path.path}`,
    }))
    .filter(
      (f) =>
        f.item.indexOf(
          `${window.location.origin}${window.location.pathname}`,
        ) === -1,
    );

  const structuredData = {
    '@context': 'https://schema.org',
    '@type': 'BreadcrumbList',
    itemListElement: itemListElement,
  };

  return (
    <Helmet>
      <script type="application/ld+json">
        {JSON.stringify(structuredData)}
      </script>
    </Helmet>
  );
};

const Breadcrumbs = ({
  structuredDataOnly,
  paths,
  className,
}: BreadcrumbsProps) => {
  const platformIsCordova = isCordova();
  if (paths.length < 2) {
    return null;
  }

  return (
    <>
      <BreadcrumbsHelmet paths={paths} />
      {!structuredDataOnly && (
        <ol className={classnames(CSS.Breadcrumbs, className)}>
          {paths.map(({ text, path, openInNewTab }, index) => {
            const isLastItem = index === paths.length - 1;
            const key = `${text}-${path}`;
            const target =
              !platformIsCordova && openInNewTab ? '_blank' : '_self';
            return (
              <li key={key} className={CSS.ListItem}>
                {!isLastItem ? (
                  <LocalizedLink
                    to={path}
                    className={CSS.Link}
                    target={target}
                    title={text}
                  >
                    <span>{text}</span>
                  </LocalizedLink>
                ) : (
                  <span title={text} className={CSS.NoLinkItem}>
                    {text}
                  </span>
                )}
                {isLastItem ? '' : `›`}
              </li>
            );
          })}
        </ol>
      )}
    </>
  );
};

export default Breadcrumbs;
